import { motion } from "framer-motion";
import { Recycle, Sparkles, Wallet, MessageCircle, ArrowUpRight } from "lucide-react";
import { IMAGES } from "../lib/images";
import { waGeneralLink, WA_DISPLAY } from "../lib/api";

const reveal = (delay) => ({
    initial: { opacity: 0, y: 36 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay },
});

const CARDS = [
    {
        title: "Reduksi Sampah Kertas",
        badge: "Eco Impact",
        chip: "bg-brand-forestLight text-brand-forest",
        icon: Recycle,
        span: "md:col-span-2",
        desc: "Mengubah kardus, koran, kertas HVS, dan majalah bekas menjadi produk fungsional yang estetis — dari keranjang anyam sampai mainan cat dari bubur kertas.",
        image: IMAGES.workshop,
        imageAlt: "Lokakarya kreator muda Acture menggulung kertas bekas jadi karya",
    },
    {
        title: "Kreativitas Generasi Muda",
        badge: "Youth Power",
        chip: "bg-brand-sunshine/70 text-brand-ink",
        icon: Sparkles,
        desc: "Wadah ekspresi seni dan kerajinan tangan pemuda Indonesia untuk memamerkan produk unik buatan sendiri.",
    },
    {
        title: "Nilai Ekonomi Tambahan",
        badge: "Economic Value",
        chip: "bg-[#FFE4D3] text-[#C24D0A]",
        icon: Wallet,
        desc: "Membuka peluang penghasilan bagi kreator muda melalui penjualan karya handmade bernilai.",
    },
    {
        title: "Konsultasi & Order via WhatsApp",
        badge: "Direct WA Order",
        chip: "bg-[#DFF7E6] text-[#128C7E]",
        icon: MessageCircle,
        span: "md:col-span-2",
        desc: "Proses order tanpa ribet — langsung terhubung ke WhatsApp kreator atau admin untuk kustomisasi, stok, dan pengiriman.",
        wa: true,
    },
];

export default function Mission() {
    return (
        <section id="misi" className="scroll-mt-24 py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div {...reveal(0)} className="max-w-2xl">
                    <span className="inline-block px-3 py-1 rounded-full border-2 border-brand-ink bg-white text-[11px] font-extrabold uppercase tracking-[0.18em] shadow-hard-sm">Misi Kami</span>
                    <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
                        Kenapa Acture<span className="text-brand-forest">.id</span>?
                    </h2>
                    <p className="mt-3 font-hand text-2xl text-brand-tomato rotate-[-1.5deg]">karena kertas bekas juga bisa glow up!</p>
                </motion.div>

                <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-4 gap-5 md:gap-7 items-stretch">
                    {CARDS.map((c, i) => (
                        <motion.article
                            key={c.title}
                            {...reveal(0.08 * i)}
                            whileHover={{ y: -6 }}
                            className={`relative rounded-[26px] border-2 border-brand-ink bg-white p-7 shadow-hard-sm hover:shadow-hard transition-shadow flex flex-col ${c.span || ""}`}
                        >
                            <span className={`self-start text-[11px] font-extrabold uppercase tracking-[0.16em] px-3 py-1 rounded-full border-2 border-brand-ink ${c.chip}`}>
                                {c.badge}
                            </span>
                            <c.icon className="w-8 h-8 mt-5 text-brand-ink" strokeWidth={2.2} />
                            <h3 className="font-display text-xl sm:text-2xl font-bold mt-3">{c.title}</h3>
                            <p className="text-sm text-brand-ink/70 mt-2 leading-relaxed">{c.desc}</p>
                            {c.image && (
                                <img src={c.image} alt={c.imageAlt} className="mt-5 rounded-2xl border-2 border-brand-ink object-cover h-44 w-full" loading="lazy" />
                            )}
                            {c.wa && (
                                <a
                                    href={waGeneralLink()}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="mt-5 inline-flex w-fit items-center gap-2 px-5 py-3 rounded-full border-2 border-brand-ink bg-brand-wa text-white font-bold text-sm shadow-hard-sm hover:-translate-y-0.5 hover:shadow-hard transition-all active:translate-y-0 active:shadow-hard-sm"
                                >
                                    Chat Admin {WA_DISPLAY} <ArrowUpRight className="w-4 h-4" strokeWidth={2.6} />
                                </a>
                            )}
                        </motion.article>
                    ))}

                    <motion.div
                        {...reveal(0.24)}
                        className="md:col-span-2 rounded-[26px] border-2 border-brand-ink bg-brand-sunshine/50 p-7 shadow-hard-sm flex flex-col justify-between"
                    >
                        <div>
                            <h3 className="font-display text-2xl font-bold">Siap jual karya daur ulangmu?</h3>
                            <p className="text-sm text-brand-ink/70 mt-2 leading-relaxed">
                                Daftar gratis, upload fotonya, tentukan harga — kami bawa karyamu ke pembeli.
                            </p>
                        </div>
                        <a
                            href="#katalog"
                            onClick={(e) => { e.preventDefault(); document.getElementById("katalog")?.scrollIntoView({ behavior: "smooth" }); }}
                            className="mt-6 inline-flex w-fit items-center gap-2 px-5 py-3 rounded-full border-2 border-brand-ink bg-white font-bold text-sm shadow-hard-sm hover:-translate-y-0.5 hover:shadow-hard transition-all active:translate-y-0 active:shadow-hard-sm"
                        >
                            Lihat dulu katalognya <ArrowUpRight className="w-4 h-4" strokeWidth={2.6} />
                        </a>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
